import * as Sentry from '@sentry/browser';
import { appConfig } from '../../../../helpers/app-config/app-config';
import type { QueryClient, QueryObserverResult } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router';
import { useCheckDomain, useLogin, useRefreshToken } from '../../../../api/auth-client/auth-client.hooks';
import type { LoginMutation, LoginResponse } from '../../../../api/auth-client/auth-client.type';
import { BackendError } from '../../../../api/auth-client/auth-client.type';
import { getOrganizations } from '../../../../api/organization-client/organization-client';
import { useOrganizations } from '../../../../api/organization-client/organization-client.hooks';
import type { OrganizationResponse } from '../../../../api/organization-client/organization-client.type';
import { fetchPortfolios } from '../../../../api/portfolio-client/portfolio-client';
import { usePortfolios } from '../../../../api/portfolio-client/portfolio-client.hooks';
import type { Portfolio } from '../../../../api/portfolio-client/portfolio-client.type';
import { fetchUserInfo } from '../../../../api/user-client/user-client';
import { useUser } from '../../../../api/user-client/user-client.hooks';
import type { UserInfo } from '../../../../api/user-client/user-client.type';
import { DEFAULT_ROUTE } from '../../../../constants';
import { UserContext } from '../../../../contexts';
import { getEmailDomain } from '../../../../helpers';
import { isSafeUrl, redirectToBackendOAuthNext, stashOAuthNext } from '../../../../helpers/general-helpers';
import {
  clearAuthenticationStore,
  getRefreshToken,
  isValidAccessToken,
  storeAuthenticationObject,
} from '../../../../helpers/storage/storage';
import {
  setCredentials,
  setSignInStep,
  setSignUpInitialized,
  setSignUpStep,
} from '../../../../store/auth-store/auth-store.actions';
import { SignInStep, SignUpStep } from '../../../../store/auth-store/auth-store.type';
import { initGlobalState, setOrganizations, setPortfolios } from '../../../../store/global-store/global-store.actions';
import { ValidationError } from '../../sign-up/sign-up.type';
import type { LoginForm } from './login.type';

/**
 * Hook to handle authentication token refresh flow.
 *
 * Responsibilities:
 * 1. Skip refresh when SSO tokens are present in URL (handled by useAuthParams)
 * 2. Skip refresh when the access token is already valid — the load-time
 *    SessionBootstrap gate owns silent restore, so refreshing here would only
 *    rotate a freshly issued token again (and a failure of that redundant
 *    request would needlessly clear the just-restored session)
 * 3. Otherwise trigger a refresh of the stored refresh token; the refresh
 *    singleton (useRefreshToken's queryFn) persists the rotated tokens on
 *    success and refuses to persist a stale result when the session changed
 *    mid-flight, so this hook does NOT write tokens itself — a second write
 *    here would bypass that guard and could resurrect a cleared session
 * 4. Clear the authentication store on refresh error (unless an SSO callback is
 *    concurrently in flight)
 *
 * @returns {void}
 */
const useAuthTokenRefresh = (): void => {
  const [searchParams] = useSearchParams();
  const token = getRefreshToken();

  // Check if SSO tokens are present in URL params - if so, skip refreshing old tokens
  // as useAuthParams will handle storing the new SSO tokens
  const hasSSOTokensInURL = !!(searchParams.get('refresh') && searchParams.get('access'));

  // `data` is intentionally not read: the singleton already persists the
  // rotated tokens (guarded against stale writes when the session changed
  // mid-flight), so this hook only reacts to the error case.
  const { error } = useRefreshToken(token || '', {
    queryKey: ['refreshToken', token] as const,
    enabled: !!token && !hasSSOTokensInURL && !isValidAccessToken(),
  });

  useEffect(() => {
    // Don't clear auth store if SSO tokens are present - let useAuthParams handle it
    if (error && !hasSSOTokensInURL) {
      clearAuthenticationStore();
    }
  }, [error, hasSSOTokensInURL]);
};

/**
 * Hook to handle authentication parameters from URL search params
 *
 * This hook manages the SSO authentication callback by:
 * 1. Checking URL search params for refresh token, access token and errors
 * 2. Handling error cases by calling provided error callback
 * 3. On successful auth, storing tokens, fetching user info, and navigating
 * 4. Cleaning up OAuth-related URL params
 *
 * @param {Function} onError - Callback function to handle authentication errors
 * @returns {void}
 */
const useAuthParams = (onError: (message: string) => void): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setUser } = useContext(UserContext);

  useEffect(() => {
    const refresh = searchParams.get('refresh');
    const access = searchParams.get('access');
    const error = searchParams.get('error');

    if (error) {
      // A failed/cancelled SSO attempt must not leave a stale oauth_next stash
      // that a later, unrelated login could consume.
      stashOAuthNext(null);
      const errorMessage =
        error === 'invalid_response' ? ValidationError.SSOInvalidResponse : ValidationError.SSOInvalidAuthentication;
      onError(errorMessage);
    } else if (refresh && access) {
      // Clear all stale session data to prevent previous session data from interfering with the new SSO login
      // and clear the authentication store before storing the new SSO tokens
      queryClient.clear();
      clearAuthenticationStore();

      storeAuthenticationObject({ access, refresh });

      searchParams.delete('code');
      searchParams.delete('state');
      searchParams.delete('refresh');
      searchParams.delete('access');
      setSearchParams(searchParams);

      // Fetch user info and handle post-login flow, mirroring the password login behavior.
      // This is necessary because UserProvider's auto-fetch won't trigger after SSO callback:
      // tokens are stored AFTER initial render, and UserProvider doesn't re-render when the
      // route changes since it sits above the router's context consumers in the component tree.
      handleSSOLoginSuccess(queryClient, navigate, setUser);
    }
  }, [searchParams, setSearchParams, navigate, onError, queryClient, setUser]);
};

/**
 * Hook to check domain authentication method and handle SSO redirects
 *
 * This hook:
 * 1. Checks the authentication method configured for the email domain
 * 2. If SSO is configured, redirects to the SSO provider URL
 * 3. If password auth is configured, calls the provided callback
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.email - User's email address to check domain configuration
 * @param {Function} params.onPasswordAuth - Callback function called when password authentication is required
 * @returns {Object} Object containing checkDomainRefetch function to trigger domain check
 */
const useAuthCheckDomain = ({ email, onPasswordAuth }: { email: string; onPasswordAuth: () => void }) => {
  const { data: checkDomainData, refetch: checkDomainRefetch } = useCheckDomain(getEmailDomain(email), {
    queryKey: ['checkDomain', email] as const,
    enabled: false,
  });

  useEffect(() => {
    if (checkDomainData) {
      const { url, authentication_method, organization_id } = checkDomainData;
      // Record (or clear) the oauth_next stash for THIS login attempt before any
      // SSO bounce: the SSO IdP round-trip returns to the FE root without
      // oauth_next, so a valid one is stashed here and restored on the token
      // landing — and a clean-URL login clears a stale stash from an abandoned
      // earlier flow so it can't be consumed by this unrelated login.
      stashOAuthNext(new URLSearchParams(window.location.search).get('oauth_next'));

      if (
        url &&
        url !== '' &&
        authentication_method !== 'password' &&
        authentication_method !== 'saml' &&
        isSafeUrl(url)
      ) {
        // OpenID Connect login
        window.location.href = url;
      } else if (authentication_method === 'saml') {
        // SAML login
        const baseURL = appConfig.VITE_APP_API_URL + '/api/';
        window.location.href = baseURL + 'saml/login/?organization_id=' + organization_id;
      } else {
        // Password login
        onPasswordAuth();
      }
    }
  }, [checkDomainData, onPasswordAuth]);

  return { checkDomainRefetch };
};

/**
 * Hook to handle login mutation and post-login actions
 *
 * This hook:
 * 1. Manages the login mutation and its success/error states
 * 2. On successful login, fetches user info and organization data
 * 3. Sets up Sentry context with user information
 * 4. Handles navigation based on number of available organizations
 * 5. Handles form error states for invalid credentials
 *
 * @param {LoginForm} form - The login form instance to handle error states
 * @returns {{ loginMutation: LoginMutation }} Object containing the login mutation
 */
const useLoginMutation = (form: LoginForm): { loginMutation: LoginMutation } => {
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);

  const { query: userInfoQuery } = useUser({ queryKey: ['userInfo'] as const, enabled: false });
  const { query: organizationsQuery } = useOrganizations({ enabled: false });
  const { query: portfoliosQuery } = usePortfolios({ enabled: false });
  const queries = { userInfoQuery, organizationsQuery, portfoliosQuery };

  const loginMutation = useLogin({
    onSuccess: async (data) => {
      if (data.access) {
        await handleLoginSuccess(queries, navigate, setUser);
      } else {
        handleLoginFailure(data, form, navigate);
      }
    },
    onError: ({ error }: { error: { detail: string } }) => {
      form.setFieldError('email', normalizeAuthError(error.detail));
      Sentry.captureException(error);
    },
  });

  return { loginMutation };
};

/**
 * Collapses backend auth-error messages that leak account existence into a
 * single generic string. Other messages (rate limits, server errors, network
 * failures) pass through unchanged so users still see actionable info.
 *
 * Insight Compliance pen test ID 02 documented that the backend returned
 * BackendError.AuthenticationFailed for non-existent accounts (HTTP 400) and
 * BackendError.InvalidCredentials for existing-but-wrong-password accounts
 * (HTTP 401), with distinct messages — letting attackers enumerate valid
 * emails. The bloomfilter-backend repo has since collapsed these to a single
 * 401 + "No active account found with the given credentials" response (see
 * the TokenObtainSerializer simplification). This helper is therefore
 * defense-in-depth: still required so a future backend regression cannot
 * leak through the UI, but ID 02 is closed primarily at the source.
 *
 * Uses `includes` (substring match) rather than strict equality so a backend
 * tweak that adds whitespace or trailing punctuation cannot silently re-open
 * the enumeration vector. Security false-positives (collapsing an unrelated
 * message that happens to embed an enumeration substring) are strictly better
 * than false-negatives that leak account existence.
 *
 * @param {string} message - Raw error string from backend `detail` or JS Error.message
 * @returns {string} Generic credentials error if `message` contains either of
 *   the two enumeration strings, otherwise `message` returned unchanged
 */
const normalizeAuthError = (message: string): string => {
  if (
    typeof message !== 'string' ||
    message.length === 0 ||
    !(message.includes(BackendError.AuthenticationFailed) || message.includes(BackendError.InvalidCredentials))
  ) {
    return message;
  }
  return ValidationError.InvalidCredentials;
};

/**
 * Handles login failure scenarios by processing error responses and updating form errors.
 *
 * `auth-client.ts login()` swallows axios errors and resolves with the response body, so
 * credential failures flow into the mutation's `onSuccess` (with `data.access` undefined),
 * not `onError`. This function is therefore the LIVE enumeration guard for pen-test ID 02 —
 * it routes both `BackendError.AuthenticationFailed` (non-existent account) and
 * `BackendError.InvalidCredentials` (existing account, wrong password) through
 * `normalizeAuthError`, which collapses both to `ValidationError.InvalidCredentials`.
 * Do NOT remove the `normalizeAuthError` call without first re-routing the live path.
 *
 * @param {LoginResponse} data - The login response data containing error details
 * @param {LoginForm} form - The login form instance to set field errors on
 * @param {NavigateFunction} navigate - React Router navigation function for redirects
 * @returns {void}
 */
const handleLoginFailure = (data: LoginResponse, form: LoginForm, navigate: NavigateFunction): void => {
  if (data.detail === BackendError.OTPNotVerified) {
    handleOTPNotVerified(form, navigate);
    return;
  }
  if (data.detail) {
    const normalized = normalizeAuthError(data.detail);
    if (normalized === ValidationError.InvalidCredentials) {
      form.setFieldError('email', normalized);
      return;
    }
  }
  // Handle email errors
  if (data.email?.length) {
    form.setFieldError('email', data.email[data.email.length - 1] || BackendError.UnexpectedError);
  }
  // Handle password errors
  if (data.password?.length) {
    form.setFieldError('password', data.password[data.password.length - 1] || BackendError.UnexpectedError);
  }
  // Set a generic error if no specific errors found
  if (!data.email?.length && !data.password?.length) {
    form.setFieldError('email', data.detail || BackendError.UnexpectedError);
  }
};

/**
 * Shared post-login finalization flow invoked by both password and SSO paths
 * once the user, organizations, and portfolios have been fetched.
 *
 * Responsibilities:
 * 1. Filter organizations down to those with at least one matching portfolio
 * 2. Write the filtered organizations + portfolios arrays into the global store
 * 3. Invoke initGlobalState synchronously to populate the singular
 *    organization/portfolio scope BEFORE navigate fires — so FloraProvider
 *    sees a valid orgId on first render after login
 * 4. Route the user through onboarding based on their organization membership
 *
 * Centralizing this logic prevents the password and SSO paths from drifting
 * apart when the post-login flow needs to change.
 *
 * @param {UserInfo} user - The authenticated user returned by the user-info fetch
 * @param {OrganizationResponse[]} organizations - Raw organizations array (pre-filter)
 * @param {Portfolio[]} portfolios - Portfolios array used to filter organizations
 * @param {NavigateFunction} navigate - React Router navigation function for onboarding routing
 * @returns {void}
 */
const finalizePostLoginFlow = (
  user: UserInfo,
  organizations: OrganizationResponse[],
  portfolios: Portfolio[],
  navigate: NavigateFunction,
): void => {
  const filteredOrganizations = organizations.filter((organization) =>
    portfolios.some((portfolio) => portfolio.organization.id === organization.id),
  );

  setOrganizations(filteredOrganizations);
  setPortfolios(portfolios);

  initGlobalState(filteredOrganizations, portfolios);

  handleUserOnboarding(user, navigate);
};

/**
 * Handles successful login by fetching user info and managing organization navigation
 *
 * @param {Object} queries - The queries object containing user info, organizations, and portfolios
 * @param {NavigateFunction} navigate - React Router navigation function
 * @param {Function} setUser - Function to set user information
 * @returns {Promise<void>} Promise that resolves when login success is handled
 * @throws {Error} If there is an error fetching user data
 */
const handleLoginSuccess = async (
  queries: {
    userInfoQuery: QueryObserverResult<UserInfo>;
    organizationsQuery: QueryObserverResult<OrganizationResponse[]>;
    portfoliosQuery: QueryObserverResult<Portfolio[]>;
  },
  navigate: NavigateFunction,
  setUser: (user: UserInfo) => void,
): Promise<void> => {
  const { userInfoQuery, organizationsQuery, portfoliosQuery } = queries;

  try {
    const { data: user } = await userInfoQuery.refetch();
    if (!user) return;

    trackUser(user);
    setUser(user);

    const { data: organizations = [] } = await organizationsQuery.refetch();
    const { data: portfolios = [] } = await portfoliosQuery.refetch();

    finalizePostLoginFlow(user, organizations, portfolios, navigate);
  } catch (error) {
    Sentry.captureException(error);
  }
};

/**
 * Handles successful SSO login by fetching user info and managing navigation.
 * This mirrors the password login flow in handleLoginSuccess — the SSO flow
 * needs to explicitly fetch user data and set it in context because UserProvider's
 * auto-fetch doesn't trigger when tokens are stored after the initial render.
 *
 * @param {QueryClient} queryClient - The React Query client instance
 * @param {NavigateFunction} navigate - React Router navigation function
 * @param {Function} setUser - Function to set user information in UserContext
 * @returns {Promise<void>}
 */
const handleSSOLoginSuccess = async (
  queryClient: QueryClient,
  navigate: NavigateFunction,
  setUser: (user: UserInfo) => void,
): Promise<void> => {
  try {
    const user = await queryClient.fetchQuery({ queryKey: ['user'], queryFn: fetchUserInfo });
    if (!user) {
      navigate(DEFAULT_ROUTE);
      return;
    }

    trackUser(user);
    setUser(user);

    const organizations =
      (await queryClient.fetchQuery({ queryKey: ['organizations'], queryFn: getOrganizations })) ?? [];
    const portfolios = (await queryClient.fetchQuery({ queryKey: ['portfolios'], queryFn: fetchPortfolios })) ?? [];

    finalizePostLoginFlow(user, organizations, portfolios, navigate);
  } catch (error) {
    Sentry.captureException(error);
    navigate(DEFAULT_ROUTE);
  }
};

/**
 * Handles OTP not verified error by setting credentials and navigating to sign-up
 *
 * @param {LoginForm} form - The login form instance to set credentials
 * @param {NavigateFunction} navigate - React Router navigation function
 * @returns {void}
 */
const handleOTPNotVerified = (form: LoginForm, navigate: NavigateFunction): void => {
  setCredentials(form.values.email, form.values.password);
  setSignUpStep(SignUpStep.CheckYourEmail);
  navigate('/sign-up', { replace: true });
};

/**
 * Handles navigation and state management based on user's organization membership
 *
 * Algorithm:
 * 1. Check if user has accepted terms and conditions
 *    - If not: Set signup step to terms and conditions and redirect to signup
 * 2. Check if user has any organizations
 *    - If no organizations: Set signup step to create org and redirect to signup
 * 3. Check if user has multiple organizations
 *    - If multiple orgs: Set signin step to org selection and redirect to signin
 * 4. Fully onboarded (single organization):
 *    - If this login came from the MCP OAuth flow (a valid `oauth_next`),
 *      redirect to the backend OAuth callback
 *    - Otherwise navigate to the default route
 *
 * The OAuth redirect is deliberately LAST: a user who has not accepted T&C or
 * has no organization must complete onboarding first, rather than being issued
 * an MCP OAuth token into a half-provisioned account.
 *
 * @param {UserInfo} user - The user information including organizations
 * @param {NavigateFunction} navigate - React Router navigation function
 * @returns {void}
 */
const handleUserOnboarding = (user: UserInfo, navigate: NavigateFunction): void => {
  if (!user.terms_and_conditions_accepted) {
    completeOnboarding(SignUpStep.TermsAndConditions, navigate);
    return;
  }
  if (!user.organizations?.length) {
    completeOnboarding(SignUpStep.CreateOrganization, navigate);
    return;
  }
  if (user.organizations.length > 1) {
    setSignInStep(SignInStep.SelectOrganization);
    return;
  }

  // Fully onboarded. If this login was triggered by the MCP OAuth flow, redirect
  // back to the backend OAuth callback the backend passed as `oauth_next`
  // (origin-locked and resolved against the backend origin, so a crafted value
  // can't open-redirect the user off-site after login).
  if (redirectToBackendOAuthNext()) {
    return;
  }

  navigate(DEFAULT_ROUTE);
};

/**
 * Completes the onboarding process by setting the signup step, initializing signup,
 * and navigating to the signup page
 *
 * @param {SignUpStep} step - The signup step to set
 * @param {NavigateFunction} navigate - React Router navigation function
 * @returns {void}
 */
const completeOnboarding = (step: SignUpStep, navigate: NavigateFunction): void => {
  setSignUpStep(step);
  setSignUpInitialized(true);
  navigate('/sign-up', { replace: true });
};

/**
 * Sets user information in Sentry for error tracking and identifies the user
 * in Heap analytics with their email and user ID
 *
 * @param {UserInfo} user - The user information object to track
 * @returns {void}
 */
const trackUser = (user: UserInfo): void => {
  Sentry.setUser(user);
  Sentry.setContext('user', user);
  window.heap?.identify(user.email);
  window.heap?.addUserProperties({ id: user.id });
};

export {
  handleUserOnboarding,
  normalizeAuthError,
  useAuthCheckDomain,
  useAuthParams,
  useAuthTokenRefresh,
  useLoginMutation,
};
